import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Send, Loader2, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatMessage = ({ message }) => (
  <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${message.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'}`}>
      {message.role === 'assistant' && (
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-blue-600" />
          <span className="text-xs font-semibold text-blue-600">PeptideGPT</span>
        </div>
      )}
      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
    </div>
  </div>
);

const PeptideGPT = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I\'m PeptideGPT, your AI-powered peptide research assistant. Ask me anything about peptides, their mechanisms, applications, safety, or research findings. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/peptidegpt`, {
        message: userMessage,
        session_id: sessionId
      });

      if (!sessionId && response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('PeptideGPT error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'I apologize, but I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="peptidegpt-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500/20 p-2 rounded-lg"><Sparkles className="text-blue-400" size={24} /></div>
            <div>
              <h2 className="text-xl font-bold" data-testid="peptidegpt-title">PeptideGPT</h2>
              <p className="text-sm text-gray-300">AI-Powered Peptide Research Assistant</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg" data-testid="close-peptidegpt">
            <X size={24} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
          {messages.map((message, idx) => (
            <ChatMessage key={`msg-${message.role}-${idx}`} message={message} />
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="animate-spin text-blue-600" size={16} />
                  <span className="text-sm text-gray-600">PeptideGPT is thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex gap-2">
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={handleKeyPress} placeholder="Ask about peptides, mechanisms, dosing, safety..." className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" disabled={loading} data-testid="chat-input" />
            <button onClick={handleSend} disabled={loading || !input.trim()} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-testid="send-button">
              <Send size={20} />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">PeptideGPT provides research-backed information. Always consult healthcare professionals.</p>
        </div>
      </div>
    </div>
  );
};

export default PeptideGPT;
